import React, { useState, useMemo } from "react";
import { ProgressBar, Button, Modal } from "react-bootstrap";
import { FiChevronDown, FiChevronUp, FiX, FiArrowUpRight, FiClock } from "react-icons/fi";
import { useUploadContext } from "../context/UploadContext";
import "./UploadStatusPanel.css";

const MAX_VISIBLE = 3;

function formatSpeed(bps) {
  if (!bps || bps === 0) return "--";
  if (bps < 1024) return `${bps} B/s`;
  if (bps < 1024 * 1024) return `${(bps / 1024).toFixed(1)} KB/s`;
  if (bps < 1024 * 1024 * 1024) return `${(bps / (1024 * 1024)).toFixed(1)} MB/s`;
  return `${(bps / (1024 * 1024 * 1024)).toFixed(2)} GB/s`;
}

function formatEta(sec) {
  if (sec == null) return "--";
  if (sec < 60) return `${Math.max(1, Math.round(sec))}s`;
  if (sec < 3600) return `${Math.round(sec / 60)} min`;
  const h = Math.floor(sec / 3600);
  const m = Math.round((sec % 3600) / 60);
  return m ? `${h}h ${m}min` : `${h}h`;
}

function traduzStatus(s) {
  return (
    s === "processing" ? "Processando"
    : s === "completed" ? "Concluído"
    : s === "error" ? "Erro"
    : s === "canceled" ? "Cancelado"
    : s === "queued" ? "Na fila"
    : s === "uploading" ? "Enviando"
    : s
  );
}

export default function UploadStatusPanel({ minimized, setMinimized }) {
  const {
    items = [],
    cancelUpload,
    clearFinished // limpe só os finalizados
  } = useUploadContext();

  const [showModal, setShowModal] = useState(false);

  const {
    totalProgress, finishedCount, totalCount, totalSpeedBps
  } = useMemo(() => {
    const uploading = items.filter(i => ["uploading", "queued", "processing"].includes(i.status));
    const finished = items.filter(i => ["completed", "canceled", "error"].includes(i.status));
    const totalProgress = uploading.length
      ? Math.round(uploading.reduce((sum, i) => sum + (i.progress || 0), 0) / uploading.length)
      : finished.length === items.length
        ? 100 : 0;
    const totalSpeedBps = uploading.reduce((sum, i) => sum + (i.speedBps || 0), 0);
    return {
      totalProgress,
      finishedCount: finished.length,
      totalCount: items.length,
      totalSpeedBps,
    };
  }, [items]);

  const visibleItems = minimized ? [] : items.slice(0, MAX_VISIBLE);
  const hasMore = items.length > MAX_VISIBLE;

  function RenderItem({ item }) {
    const canCancel = item.status === "uploading" || item.status === "queued";
    return (
      <div className="vd-upload-item">
        <div className="vd-item-row">
          <span className="vd-file-name" title={item.name}>{item.name}</span>
          {canCancel && (
            <Button
              size="sm"
              variant="link"
              className="vd-cancel-btn"
              onClick={() => cancelUpload(item.id)}
              title="Cancelar upload"
            >
              <FiX />
            </Button>
          )}
        </div>
        <ProgressBar
          now={item.progress}
          label={`${item.progress || 0}%`}
          className={`vd-progress vd-progress-roxa`}
          style={{ height: 12, minWidth: 140, marginTop: 3 }}
          animated={item.status === "uploading"}
          striped={item.status === "uploading"}
        />
        <div className="vd-item-meta">
          <span className={`vd-status vd-${item.status}`}>{traduzStatus(item.status)}</span>
          <span className="vd-speed">
            <FiArrowUpRight style={{ marginRight: 2, verticalAlign: -2 }} />
            {item.status === "uploading" || item.status === "processing"
              ? formatSpeed(item.speedBps)
              : "--"}
            <span style={{ margin: "0 7px", color: "#aaa" }}>|</span>
            <FiClock style={{ marginRight: 2, verticalAlign: -2 }} />
            {item.status === "uploading" || item.status === "processing"
              ? formatEta(item.etaSec)
              : "--"}
          </span>
        </div>
      </div>
    );
  }

  if (!items || items.length === 0) return null;

  return (
    <>
      <div className={`vd-panel-float${minimized ? " minimized" : ""}`}>
        <div className="vd-panel-header">
          <div className="vd-panel-header-main">
            <strong>Uploads</strong>
            <span className="vd-header-progress">
              <span className="vd-header-destaque">{finishedCount}/{totalCount}</span>
              <span className="vd-header-sep">|</span>
              <span className="vd-header-destaque">{totalProgress}%</span>
            </span>
            <span className="vd-header-speed ms-3">
              <FiArrowUpRight style={{ marginRight: 2, verticalAlign: -2 }} />
              {formatSpeed(totalSpeedBps)}
            </span>
          </div>
          <div className="vd-panel-header-actions">
            {!minimized && finishedCount > 0 && (
              <Button
                size="sm"
                variant="outline-light"
                className="vd-clear-btn"
                onClick={clearFinished}
                title="Limpar finalizados"
              >
                <FiX style={{ marginBottom: 1 }} />
                <span style={{ marginLeft: 3 }}>Limpar finalizados</span>
              </Button>
            )}
            <Button
              size="sm"
              variant="light"
              className="vd-minimize-btn ms-2"
              onClick={() => setMinimized && setMinimized(m => !m)}
              title={minimized ? "Mostrar uploads" : "Minimizar"}
            >
              {minimized ? <FiChevronUp /> : <FiChevronDown />}
            </Button>
          </div>
        </div>
        {/* LISTA */}
        {!minimized && (
          <div className="vd-panel-list">
            {visibleItems.map(item => (
              <RenderItem key={item.id} item={item} />
            ))}
            {hasMore && (
  <div className="vd-more-items">
    ...e mais {items.length - MAX_VISIBLE} uploads
    <Button
      variant="link"
      size="sm"
      className="vd-ver-todos-btn"
      onClick={() => setShowModal(true)}
    >
      Ver todos
    </Button>
  </div>
)}
          </div>
        )}
      </div>
      {/* MODAL: Todos os uploads */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="md" centered>
        <Modal.Header closeButton>
          <Modal.Title>Todos os uploads</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {items.map(item => (
            <RenderItem key={item.id} item={item} />
          ))}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Fechar
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}
